// The card pages' list (spec/dashboard.md section 7.2, R-DB-04): a virtualised list of collapsible cards over a template
// family, with the filter bar, saved filters, server search (as you type after a 300 ms pause, Enter at once: section 6.6,
// ui/useDebounced.ts), "+ Create" (the family's templates) and a Table view link.
import { observeElementRect, useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import type { Document, Parameter } from '../../api/types';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button, IconButton } from '../../ui/Button';
import { Banners, EmptyState } from '../../ui/Banner';
import { Badge, draftTone, draftWord } from '../../ui/Chip';
import { FilterBar, useUrlFilter, type FilterField, type FilterRule } from '../../ui/FilterBar';
import { ChevronDown, ChevronRight, EyeOff, Inbox, ListFilter, MoreVertical, Plus, Search, Table2 } from '../../ui/icons';
import { TextInput } from '../../ui/inputs';
import { useUrlParam } from '../../ui/Modal';
import { useSearchBox } from '../../ui/useDebounced';
import { Menu } from '../../ui/Popover';
import { isTempId, type Draft } from '../../workspace/Workspace';
import { entityKey } from '../../workspace/Workspace';
import { useContent } from '../ContentContext';
import { DocumentEditorHost } from '../DocumentEditor';
import { useDocumentActions, type MenuItemSpec } from '../DocumentActions';
import { useDocumentList } from '../useDocumentList';
import '../../pages/liveops/liveops.css';
import { createDocumentDraft, heirsOf, useEditorHost, useWidgetEnv } from '../widgets/context';

export interface CardContext {
  doc: Document;
  draft: Draft | undefined;
  edit: (path: string, value: unknown) => void;
  open: () => void;
  /** re-read the list from the server (after a command such as the A/B apply); absent on the inline cards */
  reload?: () => void;
  canWrite: boolean;
  expanded: boolean;
}

export interface CardListProps {
  title: ReactNode;
  template: string;
  includeHeirs?: boolean;
  /** the card's header line (title, badges, chips) */
  header: (ctx: CardContext) => ReactNode;
  /** the card's body, rendered when expanded */
  body: (ctx: CardContext) => ReactNode;
  /** extra filter fields beyond the parameters */
  filterFields?: FilterField[];
  /** extra "+ Create" entries (the wizards) */
  createExtras?: { key: string; label: string; run: () => void }[];
  /** the ⋮ menu gets Calendar colour */
  calendarColor?: boolean;
  /** the parameters the filter fields are built from (the root's by default) */
  params?: Parameter[];
  emptyText?: string;
  /** the id of the list (prefs, saved filters) */
  id: string;
  /** rules always sent with the filter bar's (the Overrides page hides the A/B tests' documents: `data.flag != 1`) */
  fixedRules?: FilterRule[];
  /** page-level controls (an environment switch, a range, a refresh) shown in the toolbar row after the filters */
  toolbar?: ReactNode;
  /** the empty state's icon (an inbox by default) */
  emptyIcon?: ReactNode;
  /** dialogs the page opens (a wizard), rendered inside the editor host */
  children?: ReactNode;
}

export function CardList(props: CardListProps) {
  return (
    <DocumentEditorHost>
      <CardListInner {...props} />
    </DocumentEditorHost>
  );
}

function CardListInner({ title, template, includeHeirs = true, header, body, filterFields = [], createExtras = [], calendarColor, params, emptyText, emptyIcon, id, toolbar, fixedRules, children }: CardListProps) {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const catalog = useContent();
  const { workspace } = useWidgetEnv();
  const host = useEditorHost();
  const canWrite = can('content_write') && !workspace.isProtected;
  const root = catalog.templates.get(template);
  const heirs = useMemo(() => heirsOf(catalog, template), [catalog, template]);
  const [search, setSearch] = useState('');
  const searchBox = useSearchBox(search, setSearch);
  const [rules, setRules] = useUrlFilter();
  const allRules = useMemo(() => (fixedRules && fixedRules.length > 0 ? [...fixedRules, ...rules] : rules), [fixedRules, rules]);
  const list = useDocumentList({ template, includeHeirs, search, rules: allRules, sort: { key: 'id', dir: 'desc' }, params });
  const actions = useDocumentActions({ canWrite, serverOf: list.serverOf, onReload: list.reload });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rows = list.rows;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 96,
    overscan: 6,
    measureElement: (el) => el.getBoundingClientRect().height || 96,
    // an unmeasured scroll element (jsdom, a hidden tab, a container without a laid-out height) counts as a viewport of this size, so the first cards render — the same fallback as DataGrid
    initialRect: { width: 1200, height: 800 },
    observeElementRect: (instance, cb) => observeElementRect(instance, (rect) => cb({ width: rect.width || 1200, height: rect.height || 800 })),
  });
  const toggle = (docId: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  const create = (t: string) => {
    const def = catalog.templates.get(t);
    if (!def) return;
    const draftId = createDocumentDraft(workspace, def);
    setExpanded((s) => new Set(s).add(draftId));
    void host?.open(draftId);
  };
  const fields: FilterField[] = [
    { key: 'id', label: 'Id', type: 'string', group: 'Document' },
    { key: 'unnyId', label: 'Legacy id', type: 'string', group: 'Document' },
    { key: 'export', label: 'Exported', type: 'bool', group: 'Document' },
    { key: 'tags', label: 'Tag', type: 'tag', group: 'Document' },
    { key: 'version', label: 'Version', type: 'number', group: 'Document' },
    ...(includeHeirs && heirs.length > 0 ? [{ key: 'template', label: 'Template', type: 'enum', group: 'Document', options: [root, ...heirs].filter((t): t is NonNullable<typeof t> => !!t).map((t) => ({ value: t.name, label: t.displayName ?? t.name })) } as FilterField] : []),
    ...filterFields,
  ];
  const createItems: MenuItemSpec[] = [
    ...[root, ...heirs].filter((t): t is NonNullable<typeof t> => !!t && !t.flags.includes('abstract')).map((t) => ({ key: t.name, label: t.displayName ?? t.name, onSelect: () => create(t.name) })),
    ...(createExtras.length ? [{ key: 'sep', label: '', separator: true }, ...createExtras.map((e) => ({ key: e.key, label: e.label, onSelect: e.run }))] : []),
  ];
  const virtualItems = virtualizer.getVirtualItems();
  const filtered = rules.length > 0 || search !== '';
  const typeName = root?.displayName ?? template.split('.').pop() ?? template;
  const [emptyTitle, ...emptyRest] = (emptyText ?? `No ${typeName} yet.`).split(/(?<=\.)\s+/);
  const createDisabled = !can('content_write') || workspace.isProtected || !root;
  return (
    <section className="card-page lo-cards">
      <div className="page-head">
        <h1>{title}</h1>
        {root && list.total !== null && (
          <span className="page-meta tabular">
            {list.total} document{list.total === 1 ? '' : 's'}
          </span>
        )}
        <span className="spacer" />
        <Link className="btn btn-ghost btn-s" to={`${routes.documents(org, game.slug, branch, template)}?heirs=1`}>
          <Table2 aria-hidden="true" /> Table view
        </Link>
        <Button size="s" variant="primary" onClick={(e) => setCreateAnchor(e.currentTarget)} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected || !root} aria-haspopup="menu" icon={<Plus aria-hidden="true" />}>
          Create <ChevronDown aria-hidden="true" />
        </Button>
        <Menu anchor={createAnchor} open={createAnchor !== null} onClose={() => setCreateAnchor(null)} label="Create" items={createItems} />
      </div>
      <div className="page-toolbar">
        <FilterBar id={id} fields={fields} rules={rules} onChange={setRules as (r: FilterRule[]) => void} />
        {toolbar && (
          <>
            <span className="lo-toolbar-sep" aria-hidden="true" />
            <div className="lo-toolbar-slot">{toolbar}</div>
          </>
        )}
        <span className="spacer" />
        <form
          className="lo-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            searchBox.applyNow();
          }}
        >
          <span className="lo-search-field">
            <Search aria-hidden="true" />
            <TextInput size="s" value={searchBox.text} onChange={(e) => searchBox.setText(e.target.value)} placeholder="Search…" aria-label="Search" />
          </span>
        </form>
        <Button size="s" variant="ghost" onClick={() => setExpanded(expanded.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)))} disabled={rows.length === 0}>
          {expanded.size === rows.length && rows.length > 0 ? 'Collapse all' : 'Expand all'}
        </Button>
      </div>
      {list.error && <Banners messages={[{ id: 'list-error', tone: 'danger', body: `${list.error.message} (request ${list.error.requestId})` }]} />}
      <div ref={scrollRef} className="card-scroll" data-testid={`cards-${id}`} aria-busy={list.loading || undefined}>
        <div className="lo-virtual" style={{ height: virtualizer.getTotalSize() }}>
          {virtualItems.map((item) => {
            const doc = rows[item.index]!;
            const draft = workspace.getDraft(entityKey('document', doc.id));
            const ctx: CardContext = { doc, draft, edit: (path, value) => actions.edit(doc, path, value), open: () => void host?.open(doc.id), reload: list.reload, canWrite: canWrite && draft?.status !== 'deleted', expanded: expanded.has(doc.id) };
            return (
              <div key={doc.id} ref={virtualizer.measureElement} data-index={item.index} className="lo-virtual-item" style={{ transform: `translateY(${item.start}px)` }}>
                <DocumentCardFrame ctx={ctx} onToggle={() => toggle(doc.id)} menu={actions.items(doc, { calendarColor })} header={header(ctx)}>
                  {ctx.expanded && body(ctx)}
                </DocumentCardFrame>
              </div>
            );
          })}
        </div>
        {rows.length === 0 && list.loading && (
          <div className="lo-card-skeleton" role="status" aria-label="Loading">
            {[0, 1, 2, 3].map((k) => (
              <div key={k} className="lo-card-skeleton-card">
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        )}
        {rows.length === 0 && !list.loading && !list.error &&
          (filtered ? (
            <EmptyState
              icon={<ListFilter aria-hidden="true" />}
              title="Nothing matches"
              body="No document passes the filter or the search; clear them to see the whole list."
              action={
                <Button
                  size="s"
                  onClick={() => {
                    setRules([]);
                    searchBox.setText('');
                    setSearch('');
                  }}
                >
                  Clear filter and search
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={emptyIcon ?? <Inbox aria-hidden="true" />}
              title={emptyTitle!.replace(/\.$/, '')}
              body={emptyRest.length > 0 ? emptyRest.join(' ') : undefined}
              action={
                <Button size="s" onClick={(e) => setCreateAnchor(e.currentTarget)} disabled={createDisabled} aria-haspopup="menu" icon={<Plus aria-hidden="true" />}>
                  New {typeName}
                </Button>
              }
            />
          ))}
        {rows.length > 0 && (list.loading || list.hasMore) && (
          <div className="lo-list-foot">
            {list.hasMore ? (
              <Button size="s" onClick={list.loadMore} busy={list.loading}>
                Load more
              </Button>
            ) : (
              <span className="loading-line">
                <span className="spinner" aria-hidden="true" /> Loading…
              </span>
            )}
          </div>
        )}
      </div>
      {actions.dialogs}
      {children}
    </section>
  );
}

export function DocumentCardFrame({ ctx, header, menu, onToggle, children }: { ctx: CardContext; header: ReactNode; menu: MenuItemSpec[]; onToggle: () => void; children?: ReactNode }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { doc, draft, expanded } = ctx;
  const open = useUrlParam('doc').value === doc.id;
  const classes = ['doc-card-page', draft ? draft.status : '', expanded ? 'expanded' : '', open ? 'selected' : ''].filter(Boolean).join(' ');
  return (
    <article className={classes} data-testid={`card-${doc.id}`} aria-label={`${doc.template} ${doc.unnyId ?? doc.id}`} aria-current={open || undefined}>
      <div className="card-head">
        <IconButton label={expanded ? 'Collapse' : 'Expand'} onClick={onToggle} aria-expanded={expanded}>
          {expanded ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
        </IconButton>
        <div className="card-head-main">{header}</div>
        <span className="card-ids">
          {draft && <Badge tone={draftTone[draft.status]}>{draftWord[draft.status]}</Badge>}
          {!doc.export && (
            <Badge tone="warning" title="Not exported: the SDK never receives this document">
              <EyeOff aria-hidden="true" /> Hidden
            </Badge>
          )}
          <code>{doc.unnyId ?? (isTempId(doc.id) ? 'new' : doc.id.slice(0, 8))}</code>
          {!isTempId(doc.id) && <span>v{String(doc.version)}</span>}
        </span>
        <IconButton label={`Actions for ${doc.unnyId ?? doc.id.slice(0, 8)}`} onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="menu">
          <MoreVertical aria-hidden="true" />
        </IconButton>
        <Menu anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Document actions" items={menu} />
      </div>
      {expanded && <div className="card-body">{children}</div>}
    </article>
  );
}
